import { ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, Optional, ViewContainerRef } from "@angular/core";
import { ILocationService } from "angular";
import { BaseProjections, NoteProjections, Projections } from "app/shared/model/request-parameter.model";
import { EMPTY, forkJoin, merge, Observable, of, race, Subject, Subscription } from "rxjs";
import { catchError, filter, finalize, map, mergeMap, skip, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { Note } from "../shared/note.model";
import { NoteService } from "../shared/note.service";
import { NoteExportService } from "ng/src/app/notes/shared/note-export.service";
import { PDFExportOptions } from "ng/src/app/shared/pdf-export/pdf-export.model";
import { NoteExportData } from "ng/src/app/notes/shared/note-export.model";
import { NotesTableDataRow } from "ng/src/app/notes/notes-table/notes-table.model";
import { LogService } from "ng/src/app/logs/log.service";
import { Utils } from "ng/src/app/core/utils";
import { NoteDetail, NoteDetailMetadata, QueryState } from "./note-detail.model";
import { NoteEntryStartConfig, TocMode } from "../shared/note-entry.model";
import { LogBookRootScopeService, PagedEntity } from "app/shared/model/app.model";
import { NoteMetadata } from "../shared/note-metadata.model";
import { ConfirmationService } from "primeng/api";
import { AppStateService } from "../../core/services/app-state.service";
import { LogRoleId } from "../../logs/log.model";
import { EntityPager, EntityPagerSupplier, PagedEntry } from "../../shared/entity-pager/entity-pager.model";
import { FileAttachment } from "ng/src/app/notes/shared/file-attachments/file-attachment.model";
import { HttpErrorResponse } from "@angular/common/http";
import { NoteControlService } from "ng/src/app/notes/note-controller/note-control.service";
import { PersistentDialogChildService } from "ng/src/app/shared/persistent-dialog/persistent-dialog.service";

@Component({
	selector: 'note-detail',
	templateUrl: './note-detail.component.html',
	styleUrls: ['./note-detail.component.scss']
})
export class NoteDetailComponent implements OnInit, OnDestroy {
	@Input() allowEdit = true;
	@Input() queryPagerEnabled = true;
	
	@Input()
	get detailOnly() {
		return this._detailOnly;
	}
	set detailOnly(val: boolean) {
		this._detailOnly = val;
	}
	
	@Input()
	set noteDetailId(val: number) {
		this.setupNoteById(val);
	}
	
	
	showDeviceInfo = false;
	showExportDialog = false;
	editingSectionId: number | null = null;
	hasSetNoteIdInParams = false;

	pagerHasNoNote = false;
	entityPager?: EntityPager<Note.Projections.TemplateValues> = undefined;
	normalWindowMediaQuery = window.matchMedia('(min-width: 992px)');
	startConfig: NoteEntryStartConfig | null = null;
	nonRichTextAttachments: FileAttachment[] = [];
	noteDeleteEnabled = false;
	
	noteDetail?: NoteDetail = undefined;

	private _editingNote = false;
	private _loadingNote = false;
	private readonly onDestroy$ = new Subject();
	private queryState?: QueryState;
	private _detailOnly = false;

	get note() {
		return this.noteDetail?.note;
	}
	
	get noteMetadata() {
		return this.noteDetail?.metadata;
	}

	get htmlNote() {
		return this.note?.safeHtmlNote ?? '';
	}
	
	get loadingNote() {
		return this._loadingNote;
	}

	get canEditNote() {
		return this.allowEdit && (LogService.selectedLog?.role ?? 'READ') !== 'READ' && (!this.entityPager || !this.entityPager.isLoading);
	}

	get editingNote() {
		return this._editingNote;
	}
	set editingNote(val) {
		this._editingNote = val;
		this.cd.detectChanges();
	}

	constructor(
		private noteControlService: NoteControlService,
		private noteService: NoteService,
		private noteExportService: NoteExportService,
		private confirmService: ConfirmationService,
		private cd: ChangeDetectorRef,
		private appStateService: AppStateService,
		@Inject('$location')
		private $location: ILocationService,
		@Inject('$rootScope')
		private $rootScope: LogBookRootScopeService,
		@Optional()
		private persistentDialogChildService?: PersistentDialogChildService
	) {}

	ngOnInit(): void {
		const queryParams = this.getQueryParams();
		if (queryParams && this.queryPagerEnabled) {
			const entryGetter: EntityPagerSupplier<Note.Projections.TemplateValues, Note.Query> = (params) => {
				params.projection = Projections.Notes.templateValues;
				for (const key in queryParams) {
					if (['size', 'page'].some(k => k === key)) continue;
					params[key] = queryParams[key];
				}
				return this.noteService.getLogNotes<Note.Projections.TemplateValues>(params).pipe(map(result => PagedEntry.from(result, 0)));
			}
			const listProvider = () => this.noteService.getLogNotes({ projection: BaseProjections.IdProjection });
			
			this.entityPager = new EntityPager({ index: this.$location.search()['index'], entryGetter, listProvider }) as EntityPager<Note.Projections.TemplateValues>;
			
			this.entityPager.currentItem$.pipe(
				takeUntil(this.onDestroy$),
				skip(1),
				filter(() => !this.detailOnly),
				switchMap((note) => {
					if (typeof note?.id !== 'number')
						return EMPTY;
					this.$location.search('index', this.entityPager?.currentIndex ?? 0).replace();
					return this.setupNote(of(note))
				})
			)
			.subscribe()
			
			this.queryState = {
				returnLocation: this.$location.search()['return-location'],
				queryFormat: this.$location.search()['query-format'],
				resultFormat: this.$location.search()['result-format'],
				query: this.$location.search()['query'],
			}
		}
		else if (!this.detailOnly) {
			this.updateFromUrlChanges();
		}
		
		this.appStateService.locationChanged$.pipe(
				takeUntil(this.onDestroy$),
				filter(({url}) => !(this.entityPager?.isLoading ?? false) && url.includes('note-detail'))
			)
			.subscribe(() => this.updateFromUrlChanges());
		
		merge(
			merge(
				LogBookRootScopeService.observeEvent(this.$rootScope, '$routeChangeStart'),
				LogBookRootScopeService.observeEvent(this.$rootScope, '$routeUpdate')
			).pipe(
				tap(([event, newRoute]) => {
					const hasNoteId = (param: string | number) => {
						if (!this.note) return false;
						return param.toString().includes(this.note.id.toString());
					}
					if (newRoute.$$route.originalPath !== '/note-detail' || newRoute.params['editOnLoad'] == null ||
						!Object.values(newRoute.params).some(hasNoteId)) return;
					
					LogBookRootScopeService.stopRouteChange(event);
					this.startEditingNote(parseInt(newRoute.params['segment'] as any));
				})
			),
			LogBookRootScopeService.observeEvent(this.$rootScope, '$locationChangeStart').pipe(
				tap(([event, newUrl]) => {
					const url = new URL(newUrl);
					const hash = url.hash;
					const params = new URLSearchParams(hash?.split('?')?.[1] ?? '');
					const newNoteId = this.getNoteIdByApiUrl(params.get('note') ?? '');
					if (hash.includes('note-detail') && params.get('editOnLoad') != null && newNoteId === this.note?.id) {
						this.startEditingNote(parseInt(params.get('segment') as any))
						event.preventDefault();
					}
				})
			)
		)
			.pipe(takeUntil(this.onDestroy$))
			.subscribe();
	}

	ngOnDestroy(): void {
		this.onDestroy$.next();
		
		if (this.hasSetNoteIdInParams)
			this.$location.search('noteId', null).replace();
	}
	
	setupNoteById(id: number, useLoadingNote = true) {
		this._loadingNote = useLoadingNote;
		return this.setupNote(
			this.noteService.getLogNote(id, NoteProjections.templateValues)
		).pipe(
			catchError((e: Error | HttpErrorResponse) => {
				console.warn('Error obtaining note, redirecting to /notes');
				console.error(`${e?.name} ${e?.message}`);
				this.navigateToLogHomePage();
				
				return of(e);
			}),
			finalize(() => this._loadingNote = false)
		)
			.subscribe();
	}
	
	noteEditorContainerReady(container: ViewContainerRef) {
		const details = this.noteControlService.startAndRegister(this.note!, container, this.editingSectionId ?? undefined);
		const instance = details.component.instance;
		instance.trapOnNavigation = true;
		instance.noteEntryService.locationUpdatesEnabled = false;
		
		instance.onAccessRevoked.pipe(
			takeUntil(this.onDestroy$)
		)
			.subscribe(() => this.navigateToLogHomePage());
		
		instance.backClicked
			.pipe(takeUntil(this.onDestroy$))
			.subscribe((mode: TocMode) => this.onBackClicked(mode));
		
		if (this.persistentDialogChildService) {
			this.persistentDialogChildService.closeConfirmation = (close) => {
				instance.stopEditingNote(() => close());
			}
		}
		
		merge(
			instance.onDestroy$,
			instance.notePublished
		).pipe(
			takeUntil(this.onDestroy$),
			take(1)
		)
			.subscribe(() => {
				this.editingNote = false;
				this.refreshNote();
			});
	}
	
	navigateToLogHomePage() {
		if (this.detailOnly) return;
		LogService.navigateToSelectedLogHomePage(this.$location).replace();
	}

	backToList() {
		if (!this.queryState) {
			console.warn('No query state, returning to search notes.');
			this.$location.url('/note-query');
			return;
		}
		const { returnLocation, query, queryFormat, resultFormat } = this.queryState;

		if (returnLocation === 'report') {
			const log = this.$location.search()['log'],
				report = this.$location.search()['report'],
				reportView = this.$location.search()['report-view'],
				overrides = this.$location.search()['overrides'];
			this.$location.url('/report-view')
			   .search('log', log)
			   .search('report', report)
			   .search('report-view', reportView)
			   .search('overrides', overrides);
		}
		else if (returnLocation === 'notes')
			this.$location.url('/notes')
				.search('pendingSignoff', query ? 'Y' : null)
				.search('query', null)
		else
			this.$location.url('/note-query')
				.search('query', query)
				.search('result-format', resultFormat)
				.search('query-format', queryFormat);
	}

	refreshNote(markAsLoad?: boolean) {
		return this.setupNoteById(this.note!.id, markAsLoad);
	}
	
	onExportToPdfClicked(pdfExportOptions: PDFExportOptions) {
		if (!this.note)
			return;
		
		this.noteExportService.exportToPDF(new NoteExportData(
			NotesTableDataRow.fromNotes([this.note], true, false),
			LogService.selectedLog?.name ?? '',
			{ 'Note ID': this.note.id.toString() },
			pdfExportOptions
		))
	}

	onBackClicked(tocMode: TocMode) {
		if (tocMode === TocMode.NAVIGATOR)
		{
			this.editingNote = false;
			this.refreshNote();
			
			if (this.detailOnly) return;
			
			// Remove note editor params from URL
			this.$location.search('segment', null)
				.search('template', null)
				.replace();
			
			if (!this.queryState) return;
			
			// Add note query details back to URL
			this.$location.search('return-location', this.queryState.returnLocation)
				.search('query-format', this.queryState.queryFormat)
				.search('result-format', this.queryState.resultFormat)
				.search('query', this.queryState.query)
				.replace();
		}
	}

	showDeleteDialog() {
		this.confirmService.confirm({
			key: 'confirm-note-delete-dialog',
			accept: () => {
				if (!this.note)
					return;

				this.noteService.deleteNote(this.note)
					.subscribe(() => {
						this.$location.url('/notes').search({}).replace();
					})
			}
		})
	}
	
	startEditingNote(sectionId = this.getNoteEditSection()) {
		if (!this.canEditNote || !this.note || this.editingNote) return;
		this.startConfig = {
			selectedLog: this.note.logId,
			disableLocationModification: true
		}
		this.editingSectionId = sectionId;
		this.editingNote = true;
	}
	
	private canDeleteNote(note: Note): boolean {
		const canDeleteDraft = (note && note.draft &&
			(LogService.selectedLog?.role ?? 'READ') !== 'READ' &&
			note.coCreators.some(lgUsr => lgUsr.user.id === AppStateService.currentUser?.id));
		
		return !!note && (canDeleteDraft ||
			(LogService.selectedLog?.activeLogUser?.role?.id === LogRoleId.ADMIN &&
				LogService.selectedLog.id === note.logId &&
				AppStateService.isNoteDeletionEnabledForLog(note.logId)));
	}

	private setupNote(noteSrc: Observable<Note.Projections.TemplateValues>) {
		return noteSrc.pipe(
			mergeMap(note => forkJoin({
				note: of(note),
				metadata: this.noteService.getNoteMetadata({
					'note.id': note.id,
					'keyLike': ['%.locationData%', '%.device.%']
				})
			})),
			tap(({ note, metadata }) => {
				if (note.logId !== LogService.selectedLog?.id) {
					this.navigateToLogHomePage();
					return;
				}
				this.nonRichTextAttachments = note?.attachments.filter(fa => fa.description != 'Rich Text Image');
				this.noteDeleteEnabled = this.canDeleteNote(note);
				
				const locationData = metadata.filter(data => data.key.indexOf('.locationData') >= 0);
				let hasLocationInformation = false;
				try {
					const parsedJSON = JSON.parse(locationData[0]?.stringValue ?? '{}');
					hasLocationInformation = Array.isArray(parsedJSON.features) && parsedJSON.features.length > 0;
				}
				catch (e) {}
				
				const data: NoteDetailMetadata = {
					locationData: locationData,
					deviceInfo: this.getDeviceInfo(metadata),
					hasLocationInformation
				};
				
				this.noteDetail = { note: note, metadata: data };
				this.updateUrl();
			})
		);
	}
	
	private updateUrl() {
		if (this.detailOnly) return;
		this.$location.search('noteId', this.note?.id ?? null)
			.search('note', null)
			.search('editOnLoad', null)
			.replace();
		
		this.hasSetNoteIdInParams = true;
	}

	private getQueryParams(): any | null {
		let params: any | null = null;
		let queryStr = this.$location.search()['query'];
		try {
			if (typeof queryStr === 'string') {
				params = JSON.parse(queryStr);
			}
		}
		finally {
			if(params && params['fromDate'])
			{
				params['fromTimeframe'] = params['fromDate'];
				delete params['fromDate'];
			}
		}
		return params;
	}

	private updateFromUrlChanges() {
		const params = this.$location.search();
		const noteId = parseInt(params['noteId']);
		const noteUrl = params['note'];
		const enterEdit = params['editOnLoad'];
		const sectionId = parseInt(params['segment']) ?? -1;
		
		let subscription: Subscription | null = null;
		if (typeof noteUrl === 'string') {
			subscription = this.setNoteByApiUrl(noteUrl);
		}
		else if (!isNaN(noteId) && noteId !== this.note?.id){
			subscription = this.setupNoteById(noteId);
		}
		
		return subscription?.add(() => {
			if (!enterEdit) return;
			this.startEditingNote(isNaN(sectionId) ? -1 : sectionId);
		});
	}

	private getNoteEditSection(params?: URLSearchParams): number | null {
		if (!this.note) return null;
		if (this.note.subTemplateId != null) return this.note.subTemplateId;
		
		const sectionalEditValue = params?.get?.('segment') ?? this.$location.search()['segment'];

		const parsedId = parseInt(sectionalEditValue);
		return (isNaN(parsedId)) ? null : parsedId;
	}

	private setNoteByApiUrl(api: string) {
		const noteId = this.getNoteIdByApiUrl(api);
		if (!isNaN(noteId))
			return this.setupNoteById(noteId);
		return null;
	}
	
	private getNoteIdByApiUrl(api: string) {
		return parseInt(((api.match(/(?!notes\/)(\d+$)/g) as Array<string>) ?? [''])[0])
	}

	private getDeviceInfo(metadata: PagedEntity<NoteMetadata>) {
		const getKeyDescription = (data: NoteMetadata) => {
			let key = data.key.substring(data.key.indexOf('.device.') + '.device.'.length);
			
			return `${key.charAt(0).toUpperCase()}${key.slice(1)}`;
		}

		return metadata
			.filter(data => data.key.indexOf('.device.') >= 0)
			.map(data => {
				const des = data.key.endsWith('.osVersion') ? 'OS Version' : getKeyDescription(data);
				
				return {
					...data,
					keyDescription: des
				}
			})
			.sort(Utils.createSortFunction(data => data.keyDescription))
	}
}